import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Enums / union types
// ---------------------------------------------------------------------------
//
// Canonical DB enums — source of truth is supabase/migrations/004_orders.sql
// for payment_method/status and supabase/migrations/20260404300000_066_refunds_and_disputes.sql
// for refund/dispute statuses. These types must match the DB *exactly*. Do
// not widen with forward-compat aliases — the UI helpers fall back gracefully
// for unknown values at render time, so forward-compat is not needed here.

export type PaymentMethod = "wave" | "orange_money" | "cash_on_delivery";

export type PaymentStatus = "pending" | "confirmed" | "failed";

export type RefundStatus = "pending" | "approved" | "processed" | "rejected";

// DB check constraint: ('open', 'under_review', 'resolved', 'rejected').
// The previous frontend had `investigating`/`resolved_merchant`/`resolved_customer`
// which do not exist in the DB — see migration 066.
// The `resolution` column on payment_disputes (`merchant` | `customer`) carries
// the merchant-vs-customer outcome; status is a coarse lifecycle enum.
export type DisputeStatus =
  | "open"
  | "under_review"
  | "resolved"
  | "rejected";

export type DisputeResolution = "merchant" | "customer" | null;
export type CashSessionStatus =
  | "open"
  | "pending_validation"
  | "validated"
  | "flagged";

// ---------------------------------------------------------------------------
// Transaction types
// ---------------------------------------------------------------------------

export interface PaymentTransaction {
  readonly id: string;
  readonly ref: string;
  readonly method: PaymentMethod;
  readonly status: PaymentStatus;
  readonly amount_fcfa: number;
  readonly fee_fcfa: number;
  readonly net_fcfa: number;
  readonly customer_name: string | null;
  readonly customer_phone: string | null;
  readonly order_ref: string | null;
  readonly created_at: string;
  readonly settled_at: string | null;
}

export interface PaymentListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly method?: PaymentMethod | null;
  readonly status?: PaymentStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
  readonly q?: string | null;
}

export interface PaymentListResponse {
  readonly payments: readonly PaymentTransaction[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// KPI types
// ---------------------------------------------------------------------------

export interface PaymentKPIs {
  readonly total_volume_fcfa: number;
  readonly wave_volume_fcfa: number;
  readonly orange_money_volume_fcfa: number;
  readonly cod_volume_fcfa: number;
  readonly success_rate: number;
  readonly avg_processing_time_ms: number;
  readonly pending_refunds: number;
  readonly open_disputes: number;
  /** Global count of payments in `pending` status (independent of list filters/pagination). */
  readonly pending_count: number;
}

// ---------------------------------------------------------------------------
// Refund types
// ---------------------------------------------------------------------------

export interface Refund {
  readonly id: string;
  readonly payment_id: string;
  readonly order_ref: string;
  readonly amount_fcfa: number;
  readonly reason: string;
  readonly status: RefundStatus;
  readonly requested_at: string;
  readonly processed_at: string | null;
  readonly processed_by: string | null;
}

export interface RefundListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: RefundStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface RefundListResponse {
  readonly refunds: readonly Refund[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface CreateRefundInput {
  readonly payment_id: string;
  readonly amount_fcfa: number;
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// Dispute types
// ---------------------------------------------------------------------------

export interface DisputeTimelineEntry {
  readonly id: string;
  readonly type: "status_change" | "note" | "communication";
  readonly message: string;
  readonly author: string;
  readonly created_at: string;
}

export interface Dispute {
  readonly id: string;
  readonly payment_id: string;
  readonly order_ref: string;
  readonly amount_fcfa: number;
  readonly reason: string;
  readonly status: DisputeStatus;
  readonly resolution: DisputeResolution;
  readonly opened_at: string;
  readonly resolved_at: string | null;
  readonly customer_name?: string;
  readonly customer_phone?: string;
  readonly timeline?: readonly DisputeTimelineEntry[];
}

export interface DisputeListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: DisputeStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface DisputeListResponse {
  readonly disputes: readonly Dispute[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// Reconciliation types
// ---------------------------------------------------------------------------

export interface ReconciliationSummary {
  readonly date: string;
  readonly provider: string;
  readonly total_transactions: number;
  readonly total_amount_fcfa: number;
  readonly total_fees_fcfa: number;
  readonly total_net_fcfa: number;
  readonly matched: number;
  readonly unmatched: number;
  readonly discrepancy_fcfa: number;
}

export interface ReconciliationListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
  readonly provider?: string | null;
}

export interface ReconciliationListResponse {
  readonly summaries: readonly ReconciliationSummary[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// Daily cash types
// ---------------------------------------------------------------------------

export interface DailyCashSession {
  readonly id: string;
  readonly date: string;
  readonly agent_name: string;
  readonly agent_id: string;
  readonly cod_collected_fcfa: number;
  readonly cod_handed_over_fcfa: number;
  readonly mobile_money_collected_fcfa: number;
  readonly expected_total_fcfa: number;
  readonly actual_total_fcfa: number;
  readonly discrepancy_fcfa: number;
  readonly status: CashSessionStatus;
  readonly orders_count: number;
}

export interface DailyCashListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: CashSessionStatus | null;
  readonly agent_id?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface DailyCashResponse {
  readonly sessions: readonly DailyCashSession[];
  readonly summary: {
    readonly total_collected: number;
    readonly total_handed_over: number;
    readonly total_discrepancy: number;
    readonly flagged_count: number;
  };
}

// ---------------------------------------------------------------------------
// COD trust score types
// ---------------------------------------------------------------------------

export interface CODTrustScore {
  readonly agent_id: string;
  readonly agent_name: string;
  readonly score: number;
  readonly collections_count: number;
  readonly discrepancy_rate: number;
  readonly last_flag_at: string | null;
}

// ---------------------------------------------------------------------------
// Callback log types
// ---------------------------------------------------------------------------

export interface CallbackLog {
  readonly id: string;
  readonly provider: string;
  readonly transactionRef: string | null;
  readonly signatureValid: boolean | null;
  readonly ipAddress: string | null;
  readonly idempotencyKey: string | null;
  readonly processedAt: string | null;
  readonly error: string | null;
  readonly createdAt: string;
}

export interface CallbackLogDetail extends CallbackLog {
  readonly rawPayload: Record<string, unknown> | null;
}

export interface CallbackListParams {
  readonly provider?: string;
  readonly page?: number;
  readonly limit?: number;
}

export interface CallbackListResponse {
  readonly callbacks: readonly CallbackLog[];
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

// ---------------------------------------------------------------------------
// Dead letter types
// ---------------------------------------------------------------------------

export interface DeadLetterEventSummary {
  readonly type: string;
  readonly paymentId: string | null;
  readonly payload: Record<string, unknown>;
  readonly createdAt: string;
}

export interface DeadLetterEntry {
  readonly id: string;
  readonly eventId: string;
  readonly attempts: number;
  readonly lastError: string | null;
  readonly createdAt: string;
  readonly event: DeadLetterEventSummary | null;
}

export interface DeadLetterListResponse {
  readonly deadLetters: readonly DeadLetterEntry[];
  readonly total: number;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const paymentApi = {
  transactions: {
    list: (params: PaymentListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        method: params.method,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
        q: params.q,
      });
      return adminFetch<PaymentListResponse>(`/api/admin/payments${qs}`);
    },

    getKPIs: () =>
      adminFetch<PaymentKPIs>("/api/admin/payments/kpis"),

    confirm: (id: string) =>
      adminFetch<{ payment: PaymentTransaction }>(
        `/api/admin/payments/${encodeURIComponent(id)}/confirm`,
        { method: "PATCH" },
      ),

    refund: (id: string) =>
      adminFetch<{ payment: PaymentTransaction }>(
        `/api/admin/payments/${encodeURIComponent(id)}/refund`,
        { method: "PATCH" },
      ),
  },

  refunds: {
    list: (params: RefundListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<RefundListResponse>(`/api/admin/payments/refunds${qs}`);
    },

    create: (input: CreateRefundInput) =>
      adminFetch<{ refund: Refund }>("/api/admin/payments/refunds", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    approve: (id: string) =>
      adminFetch<{ refund: Refund }>(
        `/api/admin/payments/refunds/${encodeURIComponent(id)}/approve`,
        { method: "PATCH" },
      ),

    reject: (id: string, reason: string) =>
      adminFetch<{ refund: Refund }>(
        `/api/admin/payments/refunds/${encodeURIComponent(id)}/reject`,
        {
          method: "PATCH",
          body: JSON.stringify({ reason }),
        },
      ),
  },

  disputes: {
    list: (params: DisputeListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<DisputeListResponse>(
        `/api/admin/payments/disputes${qs}`,
      );
    },

    resolve: (id: string, resolution: "merchant" | "customer") =>
      adminFetch<{ dispute: Dispute }>(
        `/api/admin/payments/disputes/${encodeURIComponent(id)}/resolve`,
        {
          method: "PATCH",
          body: JSON.stringify({ resolution }),
        },
      ),

    investigate: (id: string) =>
      adminFetch<{ dispute: Dispute }>(
        `/api/admin/payments/disputes/${encodeURIComponent(id)}/investigate`,
        { method: "PATCH" },
      ),

    getDetail: (id: string) =>
      adminFetch<{ dispute: Dispute }>(
        `/api/admin/payments/disputes/${encodeURIComponent(id)}`,
      ),

    addNote: (id: string, message: string) =>
      adminFetch<{ entry: DisputeTimelineEntry }>(
        `/api/admin/payments/disputes/${encodeURIComponent(id)}/notes`,
        {
          method: "POST",
          body: JSON.stringify({ message }),
        },
      ),
  },

  reconciliation: {
    list: (params: ReconciliationListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        date_from: params.date_from,
        date_to: params.date_to,
        provider: params.provider,
      });
      return adminFetch<ReconciliationListResponse>(
        `/api/admin/payments/reconciliation${qs}`,
      );
    },

    trigger: (date: string) =>
      adminFetch<{ message: string }>("/api/admin/payments/reconciliation", {
        method: "POST",
        body: JSON.stringify({ date }),
      }),

    exportCsv: (params: ReconciliationListParams = {}) => {
      const qs = toQueryString({
        date_from: params.date_from,
        date_to: params.date_to,
        provider: params.provider,
        format: "csv",
      });
      return adminFetch<{ csv_url: string }>(
        `/api/admin/payments/reconciliation/export${qs}`,
      );
    },
  },

  dailyCash: {
    list: (params: DailyCashListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        status: params.status,
        agent_id: params.agent_id,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<DailyCashResponse>(
        `/api/admin/payments/daily-cash${qs}`,
      );
    },

    validate: (id: string) =>
      adminFetch<{ session: DailyCashSession }>(
        `/api/admin/payments/daily-cash/${encodeURIComponent(id)}/validate`,
        { method: "PATCH" },
      ),

    flag: (id: string, reason: string) =>
      adminFetch<{ session: DailyCashSession }>(
        `/api/admin/payments/daily-cash/${encodeURIComponent(id)}/flag`,
        {
          method: "PATCH",
          body: JSON.stringify({ reason }),
        },
      ),
  },

  cod: {
    trustScores: () =>
      adminFetch<{ scores: readonly CODTrustScore[] }>(
        "/api/admin/payments/cod-trust-scores",
      ),
  },

  callbacks: {
    list: (params: CallbackListParams = {}) => {
      const qs = toQueryString({
        provider: params.provider,
        page: params.page,
        limit: params.limit,
      });
      return adminFetch<CallbackListResponse>(
        `/api/admin/payments/callbacks${qs}`,
      );
    },

    getDetail: (id: string) =>
      adminFetch<{ callback: CallbackLogDetail }>(
        `/api/admin/payments/callbacks/${encodeURIComponent(id)}`,
      ),
  },

  deadLetter: {
    list: () =>
      adminFetch<DeadLetterListResponse>("/api/admin/payments/dead-letter"),

    retry: (id: string) =>
      adminFetch<{ success: boolean; error?: string }>(
        `/api/admin/payments/dead-letter/${encodeURIComponent(id)}/retry`,
        { method: "POST" },
      ),

    dismiss: (id: string) =>
      adminFetch<{ success: true }>(
        `/api/admin/payments/dead-letter/${encodeURIComponent(id)}/dismiss`,
        { method: "POST" },
      ),
  },
} as const;
